import angular from 'angular';
import { EventEmitter, type IEventEmitter, A9Component } from '../../system';
import template from './index.html';
import './styles.less';

import { PerfectScrollBarExternalApiClass } from '../../visual/atoms/perfect-scrollbar';
import { VerticalTabItem } from '../tabConstructor';
import { type IVerticalTabLayoutScope } from '../vertical-tab-layout';
import { typedFastCopy } from '../../utils';

export interface ITitleTemplateItem {
    scope: angular.IScope;
    template: string;
}

export interface IVerticalTabMenuComponent extends IEventEmitter {
    externalSelectTab(path: MenuIdPath): boolean;

    /**
     * Selects a sub-tab by its unique ID.
     *
     * This method attempts to locate the tab within the current `$scope.tabs`
     * collection using the provided `id`. If the tab exists, it builds the full
     * path to the tab and triggers `$scope.selectTab` to activate it.
     *
     * @param {string} id - The unique identifier of the sub-tab to select.
     * @returns {boolean} - Returns `true` if the sub-tab was found and selected,
     *                      otherwise `false`.
     */
    externalSelectSubTabById(id: string): boolean;

    selectDefaultTab();

    hasPath(path: MenuIdPath): boolean;

    isSelectedSomething(): boolean;

    activePath(): MenuIdPath;

    // isSelectedSubTab(subTab: string);
}

export type MenuIdPath = string[];
export type MenuPath = VerticalTabItem[];

/**
 @api.emits
  select [tabObject|null, subTabObject|null]
 */

interface IScope extends angular.IScope {
    $parent: IVerticalTabLayoutScope;

    tabs: VerticalTabItem[];
    titleHeader: ITitleTemplateItem;
    titleFooter: ITitleTemplateItem;
    state: {
        path: MenuIdPath;
        menuScrollPositionY: number;
    };
    onReady: any;
    autoSelect: string;
    selectable: string;

    loading: boolean;

    selectTab(tab: VerticalTabItem, force?: boolean): void;

    isExpandable(tab: VerticalTabItem): boolean;

    isExpanded(tab: VerticalTabItem): boolean;

    isSelected(tab: VerticalTabItem): boolean;

    parentScope: angular.IScope;
    activeContent: string | undefined;
    scrollbarActive: boolean;

    dlgToggleLayout(): void;

    onMenuScrollbarScroll(): void;

    connectMenuScrollbarApi(api: PerfectScrollBarExternalApiClass): void;

    connectScrollbarApi(api: PerfectScrollBarExternalApiClass): void;

    contentPadding: boolean;
}

@A9Component({
    selector: 'vertical-tab-menu',
    replace: true,
    scope: {
        tabs: '<',
        titleHeader: '<?',
        titleFooter: '<?',
        state: '=',
        onReady: '&',
        autoSelect: '@',
        selectable: '@'
    },
    template() {
        return template;
    }
})
class VerticalTabMenuComponent extends EventEmitter implements IVerticalTabMenuComponent {
    protected activeElement: VerticalTabItem;
    protected scrollbarApi: PerfectScrollBarExternalApiClass;
    protected menuScrollbarApi: PerfectScrollBarExternalApiClass;

    constructor(
        protected $scope: IScope,
        protected $injector: angular.auto.IInjectorService,
        protected $rootScope: angular.IScope,
        protected $element: JQuery
    ) {
        super();
        this.initScope();
        this.initState();
        this.run();
        $scope.loading = true;
        $scope.$on('$destroy', () => {
            this.erase();
        });
    }

    public externalSelectTab(path: MenuIdPath): boolean {
        const $scope = this.$scope,
            tabs = this.findTabPath(path);
        if (tabs) {
            $scope.selectTab(tabs[tabs.length - 1], true);
            return true;
        }

        return false;
    }

    /**
     * Selects a sub-tab by its unique ID.
     *
     * This method attempts to locate the tab within the current `$scope.tabs`
     * collection using the provided `id`. If the tab exists, it builds the full
     * path to the tab and triggers `$scope.selectTab` to activate it.
     *
     * @param {string} id - The unique identifier of the sub-tab to select.
     * @returns {boolean} - Returns `true` if the sub-tab was found and selected,
     *                      otherwise `false`.
     */
    public externalSelectSubTabById(id: string): boolean {
        if (!this.$scope.tabs || !id) {
            return false;
        }

        const tab = this.findTabById(this.$scope.tabs, id);
        if (!tab) {
            return false;
        }

        /** Build the full path for this tab */
        const path = this.buildPathToTab(tab);
        if (path && path.length) {
            this.$scope.selectTab(tab, true);
            return true;
        }

        return false;
    }

    /**
     * Recursively searches through a list of tabs (and their sub-tabs) to find a tab by its ID.
     *
     * @param {VerticalTabItem[]} tabs - The list of tabs to search through.
     * @param {string} id - The unique identifier of the tab to locate.
     * @returns {VerticalTabItem | null} - The matching tab if found, otherwise `null`.
     */
    private findTabById(tabs: VerticalTabItem[], id: string): VerticalTabItem | null {
        for (const tab of tabs) {
            if (tab.id === id) {
                return tab;
            }
            if (tab.subTabs && tab.subTabs.length) {
                const found = this.findTabById(tab.subTabs, id);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    public selectDefaultTab() {
        const $scope = this.$scope;
        if (!$scope.tabs || !$scope.tabs.length) {
            return;
        }
        let tabs = $scope.tabs;
        const defaultPath = [];
        while (tabs && tabs.length) {
            let defaultTab = tabs.find(tab => tab.default);
            if (!defaultTab) {
                defaultTab = tabs[0];
            }
            defaultPath.push(defaultTab);
            tabs = defaultTab.subTabs;
        }
        if (!this.autoSelect()) {
            defaultPath.pop();
        }
        if (defaultPath.length) {
            $scope.selectTab(defaultPath[defaultPath.length - 1], true);
        }
    }

    public hasPath(tabPath: MenuIdPath): boolean {
        return !!this.findTabPath(tabPath);
    }

    public isSelectedSomething(): boolean {
        return this.$scope.state.path && this.$scope.state.path.length > 0;
    }

    public activePath(): MenuIdPath {
        return this.isSelectedSomething() ? typedFastCopy(this.$scope.state.path) : [];
    }

    private initScope(): void {
        const $scope = this.$scope;
        $scope.parentScope = $scope.$parent.parentScope;
        $scope.activeContent = '';
        $scope.scrollbarActive = false;
        $scope.selectTab = (tab: VerticalTabItem, force: boolean = false) => {
            if (!force && !this.selectable()) {
                return;
            }
            if (!force && tab.unselectable) {
                return;
            }
            if ($scope.isExpandable(tab)) {
                if ($scope.isExpanded(tab)) {
                    const path = $scope.state.path || [];
                    const index = path.findIndex(item => item === tab.id);
                    if (index !== -1) {
                        $scope.state.path.slice(0, index - path.length);
                    }
                } else {
                    const path = this.buildPathToTab(tab);
                    if (this.autoSelect()) {
                        let t = tab;
                        while ($scope.isExpandable(t)) {
                            t = t.subTabs[0];
                            path.push(t.id);
                        }
                    }
                    $scope.state.path = path;
                }
            } else {
                $scope.state.path = this.buildPathToTab(tab);
            }
            this.emit('select', this.findTabPath($scope.state.path));
        };

        $scope.isSelected = tab => {
            const path = $scope.state.path;
            return path && path.length && path[path.length - 1] === tab.id;
            //return path && path.length && path.indexOf(tab.id) !== -1;
        };
        $scope.isExpandable = tab => {
            return tab.subTabs && tab.subTabs.length > 0;
        };
        $scope.isExpanded = tab => {
            return $scope.isExpandable(tab) && $scope.state.path && $scope.state.path.indexOf(tab.id) !== -1;
        };
        $scope.onMenuScrollbarScroll = () => {
            $scope.state.menuScrollPositionY = this.menuScrollbarApi.getOffsetY();
        };

        $scope.connectMenuScrollbarApi = (api: PerfectScrollBarExternalApiClass) => {
            this.menuScrollbarApi = api;
            if ($scope.state.menuScrollPositionY !== null) {
                this.menuScrollbarApi.setOffsetY($scope.state.menuScrollPositionY);
                $scope.state.menuScrollPositionY = null;
            }
        };

        // todo catch detach scrollbar
        $scope.connectScrollbarApi = (api: PerfectScrollBarExternalApiClass) => {
            api.enablePointerEventsOptimization();
            this.scrollbarApi = api;
            this.activeElement.setScrollbarApi(api);
        };
    }

    private initState(): void {
        const $scope = this.$scope;
        $scope.state = $scope.state || {
            path: null,
            menuScrollPositionY: null
        };
        /*
        if ($scope.state.path) {
            this.externalSelectTab($scope.state.path); // ???
        }
         */
    }

    protected initStatePath(): void {
        const $scope = this.$scope;
        if ($scope.state.path) {
            const path = this.findTabPath($scope.state.path, false);
            if (path && path.length) {
                $scope.selectTab(path[path.length - 1], true);
            } else {
                $scope.state.path = null;
                $scope.activeContent = '';
            }
        }
        if (!$scope.state.path) {
            this.selectDefaultTab();
        }
    }

    protected run(): void {
        const $scope = this.$scope;
        $scope.onReady({ api: this });
        $scope.$watch('tabs', () => {
            if ($scope.tabs) {
                this.initStatePath();
                $scope.loading = false;
            }
        });
    }

    protected findTabPath(path: MenuIdPath, full = true): MenuPath | undefined {
        if (!this.$scope.tabs || !path || !path.length) {
            return undefined;
        }
        const result = [];
        let tabs = this.$scope.tabs;
        for (let i = 0; tabs && tabs.length && i < path.length; i++) {
            const tab = tabs.find(tab => tab.id === path[i]);
            if (tab) {
                result.push(tab);
                tabs = tab.subTabs;
            } else {
                tabs = null;
            }
        }
        return full ? (result.length === path.length && result) || undefined : (result.length && result) || undefined;
    }

    private buildPathToTab(tab: VerticalTabItem): MenuIdPath {
        const path = [tab.id];
        let parent = tab.parent;
        while (parent) {
            path.unshift(parent.id);
            parent = parent.parent;
        }
        return path;
    }

    private autoSelect() {
        return this.$scope.autoSelect !== 'false';
    }

    private selectable() {
        return this.$scope.selectable !== 'false';
    }
}
